import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SessionExpiredModal from "../components/CustomModal/SessionExpiredModal";
import SessionManager from "../services/SessionManager";
import { authAPI, TokenService } from "../services/Api";
import useAuth from "./useAuth";

const SessionProvider = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle continue - refresh token
  const handleContinue = useCallback(async () => {
    setIsLoading(true);

    try {
      // Call refresh token API
      const response = await authAPI.refreshToken();

      if (response.data?.success) {
        const newToken = response.data.data.token;

        // Store new token
        TokenService.setToken(newToken);

        // Notify SessionManager
        SessionManager.handleContinue(newToken);

        // Hide modal
        setShowModal(false);

        // Reload current page to retry the failed request
        window.location.reload();
      } else {
        // Clear tokens and redirect to login
        TokenService.clearAll();
        SessionManager.handleCancel();
        setShowModal(false);
        navigate("/login", { replace: true });
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh error:", error);

      // Clear tokens and redirect to login
      TokenService.clearAll();
      SessionManager.handleCancel();
      setShowModal(false);
      navigate("/login", { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Handle cancel - logout
  const handleCancel = useCallback(() => {
    // Clear tokens
    TokenService.clearAll();

    // Update auth context → VERY IMPORTANT
    logout();

    // Notify SessionManager
    SessionManager.handleCancel();

    // Hide modal
    setShowModal(false);

    // Navigate to login
    navigate("/login", { replace: true });
  }, [navigate, logout]);

  // Subscribe to SessionManager events
  useEffect(() => {
    const unsubscribe = SessionManager.subscribe((event) => {
      switch (event.type) {
        case "SESSION_EXPIRED":
          setShowModal(true);
          break;
        case "SESSION_REFRESHED":
        case "SESSION_CANCELLED":
        case "SESSION_INVALID":
          setShowModal(false);
          break;
        default:
          break;
      }
    });

    return () => {
      unsubscribe();
      SessionManager.reset();
    };
  }, []);

  return (
    <>
      {children}
      <SessionExpiredModal
        isOpen={showModal}
        onContinue={handleContinue}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </>
  );
};

export default SessionProvider;
